/**
 * F-126: WHERE the BGM column goes on a sheet that is divided but has no
 * music slots — pure (no React, no DOM), so the placement rule is tested
 * rather than eyeballed.
 *
 * Why this exists: "Divide into sections" mints the BGM column alongside the
 * Mood column (apply.ts), but boards divided BEFORE music slots existed have
 * their labels and nothing to put music in — and nothing offered to add it.
 * The slot layer runs this plan once the doc has synced and adds the column
 * itself (BoardSession.addSystemColumn), so a divided script always has its
 * slots without anyone hunting for a setting.
 *
 * The column id is DETERMINISTIC. Two people opening the same old board at
 * once both run this plan; `addColumn` skips an id that already exists, so
 * they converge on one column instead of racing to two.
 */
import {
  BGM_COLUMN_NAME,
  findColumnByName,
  MOOD_COLUMN_NAME,
  SECTION_COLUMN_NAME,
  SHORT_COLUMN_NAME,
  type BoardDoc,
} from "@kola/shared";

/** The one id every client uses for the column it adds on its own. */
export const AUTO_BGM_COLUMN_ID = "cbgm-auto";
/** Width of a minted BGM column — shared with apply.ts so both paths match. */
export const BGM_COLUMN_WIDTH = 190;

export interface BgmColumnPlan {
  colId: string;
  name: string;
  /** Visual position: right after Mood, else right after Section. */
  index: number;
  width: number;
}

/**
 * The column to add, or null when the sheet needs none: no Mood, Section or
 * Short column (the script is not divided and this is not a Shorts week —
 * nothing to score), or a BGM column already. F-141: a Shorts board has
 * music to find from its first open — one slot per short — so its `Short`
 * column earns the slots the way a Mood column does.
 */
export function planBgmColumn(board: BoardDoc, sheetId: string): BgmColumnPlan | null {
  const after =
    findColumnByName(board, sheetId, MOOD_COLUMN_NAME) ??
    findColumnByName(board, sheetId, SECTION_COLUMN_NAME) ??
    findColumnByName(board, sheetId, SHORT_COLUMN_NAME);
  if (after === undefined) return null;
  if (findColumnByName(board, sheetId, BGM_COLUMN_NAME) !== undefined) return null;
  const index = board.sortedColIds(sheetId).indexOf(after) + 1;
  return { colId: AUTO_BGM_COLUMN_ID, name: BGM_COLUMN_NAME, index, width: BGM_COLUMN_WIDTH };
}
